import React from "react";
import gql from "graphql-tag";
import { navigate } from "@reach/router";
import { useStoreAppValue } from "@hooks";
import { getPaginatedNodes } from "@utils";
import { constants, RailsUrl } from "@services";
import { Text, Box } from "@atoms";
import { LinkType } from "@types";
import { BrandModalLinkListItem } from "../BrandModalLinkListItem";
// import styled from "@emotion/styled";

// == Types ================================================================

interface IProps {
  brand: $FixMe;
  tinyLinkKey?: string;
  locationId?: string;
}

// == Constants ============================================================

BrandModalSimpleLinks.defaultProps = {};
BrandModalSimpleLinks.fragment = gql`
  fragment BrandModalSimpleLinksFragment on Brand {
    id
    name
    links(first: 50, linkTypes: $linkTypes, locationID: $locationId) {
      edges {
        cursor
        node {
          id
          name
          type
          giftcardType
          giveSentence
          previewInfo {
            icon
            description
            sectionName
            promotionsCount
          }
        }
      }
    }
  }
`;

const GROUP_REDUCER = (finalLinks, link) => {
  // create the first array element
  if (finalLinks.length === 0) {
    finalLinks.push({ sectionTitle: link.previewInfo.sectionName, links: [link] });
    return finalLinks;
  }

  if (finalLinks[finalLinks.length - 1].sectionTitle !== link.previewInfo.sectionName) {
    // new section
    finalLinks.push({ sectionTitle: link.previewInfo.sectionName, links: [link] });
  } else {
    // add link to existing section
    // tbh gotta test if this works
    finalLinks[finalLinks.length - 1].links.push(link);
  }
  return finalLinks;
};

const GIFTCARD_DESCRIPTIONS = (link) => {
  const { giftcardType } = link;
  switch (giftcardType) {
    case "Voucher":
      return "Get a one-time use voucher to instantly redeem for a product or service. See terms and conditions for details.";
    case "":
      return "Receive digital cards instantly to redeem online or in-store.";
    default:
      return "Receive digital gift cards instantly to spend online or in-store.";
  }
};

const LINK_DESCRIPTIONS = {
  BRAND: "Shop online directly at the retailers website on your phone or desktop.",
  AUTORELOADGIFTCARD:
    "Receive and customize reloadable gift cards instantly to spend online or in-store.",
  EMPYR: "Activate this offer and use a linked payment card to purchase in-store.",
};

const LINK_DESCRIPTION = (link, locationId?: string) => {
  // overwrite default api functionality for this hardcoded description
  if (link.type === LinkType.EMPYR && !locationId) return constants.NEARBY_LINKED_CARD_DESCRIPTION;
  if (link.previewInfo.description) return link.previewInfo.description;
  if (link.type === LinkType.GIFTCARDOFFER) return GIFTCARD_DESCRIPTIONS(link);
  if (Object.keys(LINK_DESCRIPTIONS).includes(link.type)) return LINK_DESCRIPTIONS[link.type];
  return "";
};

const LINK_LEFT_IMAGE = (link) => {
  if (link?.previewInfo?.icon) return link.previewInfo.icon;
  if (link.type === LinkType.BRAND)
    return "https://flipgive.imgix.net/images/site/shop/brand-modal-shop.png";
  if (link.type === LinkType.EMPYR)
    return "https://flipgive.imgix.net/images/site/shop/brand-modal-in-store.png";
  if (link.type === LinkType.GIFTCARDOFFER || link.type === LinkType.AUTORELOADGIFTCARD)
    return "https://flipgive.imgix.net/images/site/shop/brand-modal-giftcard-v2.png";
  return "https://flipgive.imgix.net/images/site/shop/brand-modal-shop.png";
};

// == Component ============================================================

export function BrandModalSimpleLinks({ brand, tinyLinkKey, locationId: locationIdProp }: IProps) {
  const { campaignId } = useStoreAppValue();
  const links = getPaginatedNodes(brand.links.edges);
  const groupedLinks = links.reduce(GROUP_REDUCER, []);

  // const { shopLink, giftcardLink, linkedCardLink } = brand;
  const navigateToLink = (linkId: string, locationId?: string) => {
    const brandRoute = `?${constants.brandParam}=${brand.id}`;
    let navigationRoute = `?${constants.linkParam}=${linkId}`;
    if (locationId) {
      navigationRoute += `&location_id=${locationId}`;
      navigate(navigationRoute, {
        state: { brandRoute, tinyLinkKey, brandId: brand.id, canReturn: true },
      });
    }
    navigate(navigationRoute, {
      state: { brandRoute, tinyLinkKey, brandId: brand.id, canReturn: true },
    });
  };

  const navigateToLinkedCardLink = (linkId: string, locationId?: string) => {
    if (locationIdProp) {
      navigateToLink(linkId, locationId);
    } else {
      const route = RailsUrl.nearbyUrl({
        campaignId,
        brandId: brand.id,
        brandName: brand.name,
        allLocations: true,
      });
      navigate(route);
    }
  };

  return (
    <>
      {groupedLinks.map((linkGroup) => {
        return (
          <Box key={linkGroup.sectionTitle}>
            <Box bg="lightBg" p={3}>
              <Text fontWeight="bold">{linkGroup.sectionTitle}</Text>
            </Box>
            {linkGroup.links.map((link, linkIndex) => {
              const description = LINK_DESCRIPTION(link, locationIdProp);
              const image = LINK_LEFT_IMAGE(link);

              if (link.type === LinkType.EMPYR) {
                return (
                  <BrandModalLinkListItem
                    description={description}
                    giveSentence={link.giveSentence}
                    key={link.id}
                    leftImage={image}
                    linkId={link.id}
                    promotionsCount={link.previewInfo.promotionsCount}
                    showBorder={linkIndex > 0}
                    title={link.name}
                    onClick={navigateToLinkedCardLink}
                  />
                );
              }
              return (
                <BrandModalLinkListItem
                  description={description}
                  giveSentence={link.giveSentence}
                  key={link.id}
                  leftImage={image}
                  linkId={link.id}
                  promotionsCount={link.previewInfo.promotionsCount}
                  showBorder={linkIndex > 0}
                  title={link.name}
                  onClick={navigateToLink}
                />
              );
            })}
          </Box>
        );
      })}
    </>
  );
}

// == Styles ===============================================================
